import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { AvvisoMantra } from "../common/AvvisoMantra";
import { etichetta } from "../common/etichette";
import { NOME_MODALITA_PRO } from "../common/nomenclatura";
import { tr } from "../../i18n";
interface StagioneItem {
  id: string;
  anno: string;
  modalita: "mantra" | "classic";
  stato:
    | "preparazione"
    | "mercato_estivo"
    | "lista_provvisoria"
    | "in_corso"
    | "mercato_invernale"
    | "conclusa";
  data_inizio: string | null;
  data_fine: string | null;
  budget_stagionale_milioni: number;
  budget_invernale_milioni: number;
  is_prima_stagione: boolean;
  giornata_serie_a_inizio: number;
  giornata_serie_a_fine: number;
}

interface CreaSuccessivaResponse {
  stagione_id: string;
  anno: string;
  regolamento_clonato: boolean;
  iscrizioni_clonate: number;
  capitani_clonati: number;
  movimenti_budget_creati: number;
}

const STATO_COLOR: Record<StagioneItem["stato"], string> = {
  preparazione: "bg-fg-muted/20 text-fg-muted",
  mercato_estivo: "bg-warning/20 text-warning",
  lista_provvisoria: "bg-warning/20 text-warning",
  in_corso: "bg-success/20 text-success",
  mercato_invernale: "bg-info/20 text-info",
  conclusa: "bg-danger/20 text-danger",
};

export function StagioniAdminPage() {
  useAuth(); // session check
  const qc = useQueryClient();
  const [annoNuovo, setAnnoNuovo] = useState("");
  const [dataInizio, setDataInizio] = useState("");
  // Modalita obbligatoria - default vuoto per forzare la scelta
  const [modalita, setModalita] = useState<"" | "mantra" | "classic">("");
  const [giornataInizio, setGiornataInizio] = useState<string>("1");
  const [giornataFine, setGiornataFine] = useState<string>("38");
  // Budget mercato della nuova stagione (sommato al budget esistente delle squadre)
  const [budgetStagionale, setBudgetStagionale] = useState<string>("200");
  // Toggle clone da stagione precedente (visibile solo se c'è una stagione CONCLUSA)
  const [cloneFromPrev, setCloneFromPrev] = useState(true);

  const { data: stagioni } = useQuery({
    queryKey: ["admin", "stagioni"],
    queryFn: async () => (await api.get<StagioneItem[]>("/admin/stagioni")).data,
  });

  const chiudiMut = useMutation({
    mutationFn: async (stagioneId: string) => {
      const res = await api.post<StagioneItem>(`/admin/stagioni/${stagioneId}/chiudi`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "stagioni"] }),
  });

  // Reset completo (TRUNCATE CASCADE su stagioni) — DESTRUTTIVO
  const resetAllMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        stagioni_eliminate: number;
        tabelle_truncate: number;
      }>("/admin/stagioni/reset-all", { conferma: "RESET STAGIONI" });
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "stagioni"] });
      qc.invalidateQueries({ queryKey: ["stagioni-all"] });
      alert(
        tr("✓ Reset completato:\\n") +
          tr("  - {{v1}} stagioni eliminate\\n", { v1: data.stagioni_eliminate }) +
          tr("  - {{v1}} tabelle truncate\\n\\n", { v1: data.tabelle_truncate }) +
          tr("Squadre, giocatori e utenti preservati."),
      );
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      alert(tr("Errore reset: {{v1}}", { v1: e.response?.data?.detail ?? "sconosciuto" }));
    },
  });

  // Cambia modalita di una stagione esistente (solo se regolamento non congelato)
  const cambiaModalitaMut = useMutation({
    mutationFn: async (args: { stagioneId: string; modalita: "mantra" | "classic" }) => {
      const res = await api.patch<StagioneItem>(`/admin/stagioni/${args.stagioneId}`, {
        modalita: args.modalita,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "stagioni"] });
      qc.invalidateQueries({ queryKey: ["stagioni-all"] });
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      alert(e.response?.data?.detail ?? tr("Errore cambio modalità"));
    },
  });

  // Crea stagione standalone (prima o intermedia, senza clone)
  const creaMut = useMutation({
    mutationFn: async (args: {
      anno: string;
      modalita: "mantra" | "classic";
      dataInizio: string;
      giornataInizio: number;
      giornataFine: number;
      budgetStagionale: number;
    }) => {
      const res = await api.post<StagioneItem>("/admin/stagioni", {
        anno: args.anno,
        modalita: args.modalita,
        data_inizio: args.dataInizio || null,
        giornata_serie_a_inizio: args.giornataInizio,
        giornata_serie_a_fine: args.giornataFine,
        budget_stagionale_milioni: args.budgetStagionale,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "stagioni"] });
      qc.invalidateQueries({ queryKey: ["stagioni-all"] });
      qc.invalidateQueries({ queryKey: ["stagione-giornate"] });
      setAnnoNuovo("");
      setDataInizio("");
      setModalita("");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      alert(tr("Errore: {{v1}}", { v1: e.response?.data?.detail ?? "creazione fallita" }));
    },
  });

  // Pesca info Serie A da football-data.org
  const [fbInfo, setFbInfo] = useState<{
    inizio: number;
    fine: number;
    matchdays: number;
    season_start: string | null;
    season_end: string | null;
  } | null>(null);
  const fbInfoMut = useMutation({
    mutationFn: async () => {
      const res = await api.get<{
        current_matchday: number;
        total_matchdays: number;
        season_start: string | null;
        season_end: string | null;
      }>("/admin/football-data/serie-a-info");
      return res.data;
    },
    onSuccess: (d) => {
      setFbInfo({
        inizio: d.current_matchday || 1,
        fine: d.total_matchdays || 38,
        matchdays: d.total_matchdays || 38,
        season_start: d.season_start,
        season_end: d.season_end,
      });
      // Pre-popola gli input
      setGiornataInizio(String(d.current_matchday || 1));
      setGiornataFine(String(d.total_matchdays || 38));
      if (d.season_start && !dataInizio) {
        setDataInizio(d.season_start.slice(0, 10));
      }
    },
  });

  const creaSuccessivaMut = useMutation({
    mutationFn: async (args: {
      stagioneId: string;
      annoNuovo: string;
      modalita: "mantra" | "classic";
      dataInizio: string;
      giornataInizio: number;
      giornataFine: number;
      budgetStagionale: number;
    }) => {
      const res = await api.post<CreaSuccessivaResponse>(
        `/admin/stagioni/${args.stagioneId}/crea-successiva`,
        {
          anno_nuovo: args.annoNuovo,
          modalita: args.modalita,
          data_inizio: args.dataInizio || null,
          giornata_serie_a_inizio: args.giornataInizio,
          giornata_serie_a_fine: args.giornataFine,
          budget_stagionale_milioni: args.budgetStagionale,
        },
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin", "stagioni"] });
      qc.invalidateQueries({ queryKey: ["stagioni-all"] });
      alert(
        tr("✓ Stagione {{v1}} creata.\\n", { v1: data.anno }) +
          tr("Regolamento clonato: {{v1}}\\n", { v1: data.regolamento_clonato ? "sì" : "no" }) +
          tr("Iscrizioni (rose) clonate: {{v1}}\\n", { v1: data.iscrizioni_clonate }) +
          tr("Capitani clonati: {{v1}}\\n", { v1: data.capitani_clonati }) +
          tr("Movimenti budget (dotazione): {{v1}}", { v1: data.movimenti_budget_creati }),
      );
      setAnnoNuovo("");
      setDataInizio("");
      setModalita("");
    },
    onError: (e: { response?: { data?: { detail?: string } } }) => {
      alert(tr("Errore: {{v1}}", { v1: e.response?.data?.detail ?? "creazione fallita" }));
    },
  });

  // Logica creazione stagione:
  // - hasActiveStagione = c'è già una stagione non conclusa → non si può creare nuova
  // - ultimaConclusa = ultima stagione CONCLUSA in ordine anno DESC → sorgente clone
  // - canCreate = nessuna stagione attiva (o nessuna stagione tout court)
  // - isClone = canCreate && esiste una conclusa → si clona da quella
  const hasActiveStagione = stagioni?.some((s) => s.stato !== "conclusa") ?? false;
  const ultimaConclusa =
    stagioni
      ?.filter((s) => s.stato === "conclusa")
      .sort((a, b) => b.anno.localeCompare(a.anno))[0] ?? null;
  const canCreate = !hasActiveStagione;
  const isClone = canCreate && ultimaConclusa !== null && cloneFromPrev;

  return (
    <MobileShell logoSuffix="STAGIONI">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Gestione stagioni")}</h2>

      <div className="card text-sm text-fg-muted">
        {tr(
          "Chiudi la stagione corrente e crea la successiva. Vengono clonati regolamento, iscrizioni (ufficiali → provvisorie), capitani e budget.",
        )}
      </div>

      {/* Form unificato: crea stagione (prima o successiva con clone) */}
      {canCreate && (
        <div className="card space-y-3 border border-accent/40">
          <h3 className="font-display text-xl text-accent">
            {ultimaConclusa
              ? `⭐ Crea stagione successiva${isClone ? ` (clone da ${ultimaConclusa.anno})` : ""}`
              : tr("⭐ Crea la prima stagione")}
          </h3>

          {ultimaConclusa && (
            <label className="flex items-center gap-2 text-sm bg-success/10 border border-success/30 rounded p-3">
              <input
                type="checkbox"
                checked={cloneFromPrev}
                onChange={(e) => setCloneFromPrev(e.target.checked)}
              />
              <span>
                {tr("Clona da")} <strong>{ultimaConclusa.anno}</strong>
                {tr(
                  ": regolamento, rose (giocatori), capitani. Il budget viene ricaricato (la dotazione stagionale si somma a quella esistente).",
                )}
              </span>
            </label>
          )}

          {/* Suggerimento da football-data.org */}
          <div className="bg-info/10 border border-info/30 rounded p-3 space-y-2">
            <div className="text-xs text-fg-muted">
              💡 <strong className="text-info">{tr("Suggerimento")}</strong>
              {tr(
                ": pesca automaticamente le giornate del campionato Serie A in corso da football-data.org.",
              )}
            </div>
            <button
              type="button"
              onClick={() => fbInfoMut.mutate()}
              disabled={fbInfoMut.isPending}
              className="btn-ghost text-xs border border-info/40 text-info"
            >
              {fbInfoMut.isPending ? tr("Recupero…") : tr("⚽ Pesca info Serie A")}
            </button>
            {fbInfo && (
              <div className="text-xs text-success font-mono">
                ✓ Serie A: {fbInfo.matchdays} giornate totali
                {fbInfo.season_start && <span> · inizio {fbInfo.season_start.slice(0, 10)}</span>}
                {fbInfo.season_end && <span> · fine {fbInfo.season_end.slice(0, 10)}</span>}
              </div>
            )}
            {fbInfoMut.isError && (
              <div className="text-xs text-danger space-y-1">
                <div>
                  ⚠ Errore football-data.org:{" "}
                  {(
                    fbInfoMut.error as {
                      response?: { data?: { detail?: string } };
                      message?: string;
                    }
                  ).response?.data?.detail ??
                    (fbInfoMut.error as Error).message ??
                    tr("fetch fallito")}
                </div>
                <div className="text-fg-muted">
                  Non è bloccante: puoi impostare giornata SA inizio/fine manualmente sotto. (Se
                  l'errore è "FOOTBALL_DATA_API_KEY non configurata", aggiungi la chiave nelle
                  variabili d'ambiente di Railway.)
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Anno")}
              </label>
              <input
                type="text"
                placeholder={tr("es. 2026-27")}
                value={annoNuovo}
                onChange={(e) => setAnnoNuovo(e.target.value)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              />
            </div>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Modalità *")}
              </label>
              <select
                value={modalita}
                onChange={(e) => setModalita(e.target.value as "" | "mantra" | "classic")}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              >
                <option value="">{tr("— scegli modalità —")}</option>
                <option value="mantra">{NOME_MODALITA_PRO}</option>
                <option value="classic">Classic</option>
              </select>
            </div>
          </div>
          {/* Andrea 2026-09-17: Mantra e' di Fantacalcio.it, va detto. */}
          {modalita === "mantra" && <AvvisoMantra />}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Data inizio (opz.)")}
              </label>
              <input
                type="date"
                value={dataInizio}
                onChange={(e) => setDataInizio(e.target.value)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              />
            </div>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Giornata SA inizio")}
              </label>
              <input
                type="number"
                min="1"
                max="38"
                value={giornataInizio}
                onChange={(e) => setGiornataInizio(e.target.value)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              />
            </div>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Giornata SA fine")}
              </label>
              <input
                type="number"
                min="1"
                max="38"
                value={giornataFine}
                onChange={(e) => setGiornataFine(e.target.value)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              />
            </div>
            <div>
              <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
                {tr("Budget mercato (M)")}
              </label>
              <input
                type="number"
                min="0"
                step="10"
                value={budgetStagionale}
                onChange={(e) => setBudgetStagionale(e.target.value)}
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
              />
            </div>
          </div>

          <p className="text-xs text-fg-muted">
            Verranno create{" "}
            <strong className="text-accent">
              {Math.max(0, Number(giornataFine) - Number(giornataInizio) + 1)} giornate fanta
            </strong>{" "}
            (G1 → SA {giornataInizio}, ...) ·{" "}
            <strong className="text-accent">+{budgetStagionale}M</strong>{" "}
            {tr("di budget mercato sommato a ogni squadra.")}
          </p>

          <button
            disabled={
              !annoNuovo ||
              !modalita ||
              creaMut.isPending ||
              creaSuccessivaMut.isPending ||
              Number(giornataInizio) < 1 ||
              Number(giornataFine) > 38 ||
              Number(giornataInizio) > Number(giornataFine) ||
              Number(budgetStagionale) < 0
            }
            onClick={() => {
              const baseArgs = {
                anno: annoNuovo,
                modalita: modalita as "mantra" | "classic",
                dataInizio,
                giornataInizio: Number(giornataInizio),
                giornataFine: Number(giornataFine),
                budgetStagionale: Number(budgetStagionale),
              };
              if (isClone && ultimaConclusa) {
                creaSuccessivaMut.mutate({
                  stagioneId: ultimaConclusa.id,
                  annoNuovo: baseArgs.anno,
                  modalita: baseArgs.modalita,
                  dataInizio: baseArgs.dataInizio,
                  giornataInizio: baseArgs.giornataInizio,
                  giornataFine: baseArgs.giornataFine,
                  budgetStagionale: baseArgs.budgetStagionale,
                });
              } else {
                creaMut.mutate(baseArgs);
              }
            }}
            className="btn-primary"
          >
            {creaMut.isPending || creaSuccessivaMut.isPending
              ? tr("Creazione…")
              : ultimaConclusa
                ? tr("⭐ Crea stagione successiva{{v1}}", { v1: isClone ? " (clone)" : "" })
                : tr("⭐ Crea prima stagione")}
          </button>
        </div>
      )}

      {hasActiveStagione && (
        <div className="card text-sm text-fg-muted border border-warning/30">
          ⚠ C'è già una stagione attiva. Per crearne una nuova, chiudi prima quella corrente con il
          bottone "Chiudi stagione".
        </div>
      )}

      <div className="card p-0 overflow-hidden">
        {stagioni?.length === 0 && (
          <div className="p-6 text-center text-fg-muted">{tr("Nessuna stagione.")}</div>
        )}
        {stagioni?.map((s) => (
          <div
            key={s.id}
            className="flex flex-col md:flex-row md:items-center gap-3 p-4 border-b border-primary/10 last:border-b-0"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-bold font-mono">{s.anno}</div>
                <span className={`pill ${STATO_COLOR[s.stato]}`}>{etichetta(s.stato)}</span>
                {s.is_prima_stagione && (
                  <span className="pill bg-accent/20 text-accent">PRIMA</span>
                )}
              </div>
              <div className="text-xs text-fg-muted mt-1">
                {s.data_inizio ?? "?"} → {s.data_fine ?? tr("in corso")} · giornate{" "}
                {s.giornata_serie_a_inizio}-{s.giornata_serie_a_fine}
              </div>
            </div>
            {/* Modalità: dropdown editabile finché regolamento non congelato */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-fg-muted uppercase tracking-wider">
                {tr("Modalità")}
              </label>
              <select
                value={s.modalita}
                disabled={s.stato === "conclusa" || cambiaModalitaMut.isPending}
                onChange={(e) =>
                  cambiaModalitaMut.mutate({
                    stagioneId: s.id,
                    modalita: e.target.value as "mantra" | "classic",
                  })
                }
                className="px-2 py-1 bg-primary-deep border border-primary-soft rounded text-fg text-sm font-mono"
              >
                <option value="mantra">{NOME_MODALITA_PRO}</option>
                <option value="classic">Classic</option>
              </select>
            </div>
            {s.stato !== "conclusa" && (
              <button
                onClick={() => chiudiMut.mutate(s.id)}
                disabled={chiudiMut.isPending}
                className="btn-ghost text-sm border border-danger/40 text-danger hover:bg-danger/10"
              >
                {tr("Chiudi stagione")}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* DANGER ZONE — reset completo stagioni */}
      <div className="card border border-danger/40 bg-danger/5 space-y-3">
        <h3 className="font-display text-lg text-danger">{tr("⚠ Reset stagioni (destructive)")}</h3>
        <p className="text-xs text-fg-muted">
          {tr(
            "Cancella TUTTE le stagioni + dati derivati (regolamento, voti, formazioni, movimenti, possessi, transfermarkt snapshot, ecc.).",
          )}
          <br />
          <strong className="text-fg">{tr("Preserva")}</strong>
          {tr(": utenti, squadre, anagrafica giocatori.")}
        </p>
        <button
          type="button"
          disabled={resetAllMut.isPending}
          onClick={() => {
            if (
              !confirm(
                tr("Sei sicuro? Questa azione è IRREVERSIBILE e cancellerà tutte le stagioni."),
              )
            )
              return;
            const phrase = prompt(tr("Per confermare, digita esattamente: RESET STAGIONI"));
            if (phrase !== "RESET STAGIONI") {
              alert(tr("Conferma mancata — operazione annullata."));
              return;
            }
            resetAllMut.mutate();
          }}
          className="btn-ghost border border-danger text-danger hover:bg-danger/10 disabled:opacity-50"
        >
          {resetAllMut.isPending ? tr("Reset in corso…") : tr("🗑 Cancella tutte le stagioni")}
        </button>
      </div>
    </MobileShell>
  );
}
